/** Stage E — Explain WHY panel
 * Renders the structured `why` block from an opportunity/analysis:
 *   why_now, why_this_portfolio, why_this_position_size, why_not_wait,
 *   invalidation_conditions[], monitor[], positive_catalysts[], negative_catalysts[]
 */
import { HelpCircle, ShieldAlert, Eye, TrendingUp, TrendingDown } from "lucide-react";

function Bullet({ item }) {
  return (
    <li className="text-xs text-foreground/85 leading-relaxed pl-3 relative before:content-['·'] before:absolute before:left-0 before:top-0 before:text-muted-foreground">
      {item}
    </li>
  );
}

function Prose({ title, body, icon: Icon, tone = "" }) {
  if (!body) return null;
  return (
    <div className="border-t border-border/40 pt-3">
      <div className={`label-cap flex items-center gap-1.5 mb-1 ${tone}`}>
        {Icon && <Icon size={11}/>} {title}
      </div>
      <p className="text-xs text-foreground/90 leading-relaxed">{body}</p>
    </div>
  );
}

function BulletList({ title, items, icon: Icon, tone = "" }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="border-t border-border/40 pt-3">
      <div className={`label-cap flex items-center gap-1.5 mb-1 ${tone}`}>
        {Icon && <Icon size={11}/>} {title}
      </div>
      <ul className="space-y-1">
        {items.map((i, idx) => <Bullet key={idx} item={i}/>)}
      </ul>
    </div>
  );
}

export default function WhyPanel({ why }) {
  if (!why) {
    return (
      <div className="border border-dashed border-border/40 p-4 text-xs text-muted-foreground text-center" data-testid="why-panel-empty">
        No explanation available yet — refresh the analysis to generate one.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="why-panel">
      <Prose title="Why now?" body={why.why_now} icon={HelpCircle}/>
      <Prose title="Why this portfolio?" body={why.why_this_portfolio} icon={HelpCircle}/>
      <Prose title="Why this position size?" body={why.why_this_position_size} icon={HelpCircle}/>
      <Prose title="Why not wait?" body={why.why_not_wait} icon={HelpCircle}/>
      <BulletList title="What invalidates this trade" items={why.invalidation_conditions} icon={ShieldAlert} tone="text-signal-red"/>
      <BulletList title="What to monitor" items={why.monitor} icon={Eye}/>
      <BulletList title="Events that would improve this" items={why.positive_catalysts} icon={TrendingUp} tone="pnl-up"/>
      <BulletList title="Events that would worsen this" items={why.negative_catalysts} icon={TrendingDown} tone="pnl-down"/>
    </div>
  );
}
